import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";

// The original founding three — always pinned to the top of the gallery,
// in this exact order, regardless of the user's chosen sort.
const FOUNDING_TOP_ROW = [
  "f8862626-abby",
  "08112026-mickey",
  "d50e0abf-little-bear",
  "e8f56ae1-princess",
];
const pinRank = (slug) => {
  const i = FOUNDING_TOP_ROW.indexOf(slug);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

// Umbrella species groups so families can filter by "Dog" or "Cat" even when
// the memorial's species field is a specific breed like "Border Collie mix".
const DOG_REGEX = /(dog|puppy|hound|retriever|collie|terrier|spaniel|poodle|shepherd|pug|husky|dachshund|corgi|bulldog|chihuahua|mastiff|beagle|boxer|doberman|rottweiler|labrador|\blab\b|doodle|golden|shiba|akita|malinois|schnauzer|pointer|setter|dane|dalmatian|maltese|pomeranian|yorkie|whippet|greyhound|bichon)/i;
const CAT_REGEX = /(\bcat\b|kitten|kitty|feline|siamese|persian|ragdoll|maine coon|bengal|sphynx|russian blue|shorthair|longhair|tabby|calico)/i;

const matchesUmbrella = (speciesText, umbrella) => {
  const s = (speciesText || "").trim();
  if (!s) return false;
  if (umbrella === "Dog") return DOG_REGEX.test(s);
  if (umbrella === "Cat") return CAT_REGEX.test(s);
  return false;
};

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [species, setSpecies] = useState("all");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    apiClient.get("/memorials?limit=100")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);

  const speciesOptions = useMemo(() => {
    const set = new Set();
    let hasDog = false;
    let hasCat = false;
    items.forEach((m) => {
      const s = (m.species || "").trim();
      if (s) set.add(s);
      if (matchesUmbrella(s, "Dog")) hasDog = true;
      if (matchesUmbrella(s, "Cat")) hasCat = true;
    });
    const umbrellas = [];
    if (hasDog) umbrellas.push("Dog");
    if (hasCat) umbrellas.push("Cat");
    // De-dup: drop specific species strings that collide with an umbrella value
    // (e.g. a memorial whose species is literally "Cat" or "Dog").
    const specifics = Array.from(set).filter((s) => !umbrellas.includes(s)).sort();
    return ["all", ...umbrellas, ...specifics];
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = items.filter((m) => {
      const s = (m.species || "").trim();
      if (species !== "all") {
        if (species === "Dog" || species === "Cat") {
          if (!matchesUmbrella(s, species)) return false;
        } else if (s !== species) {
          return false;
        }
      }
      if (!needle) return true;
      const hay = `${m.pet_name} ${s} ${m.owner_display_name || ""} ${m.dates || ""}`.toLowerCase();
      return hay.includes(needle);
    });
    if (sort === "name") out = [...out].sort((a, b) => a.pet_name.localeCompare(b.pet_name));
    else out = [...out].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    // Always pin the ORIGINAL founding three to the top row (Abby, Little Bear,
    // Princess), keeping the user's chosen sort intact for everyone else.
    out.sort((a, b) => pinRank(a.share_slug) - pinRank(b.share_slug));
    return out;
  }, [items, q, species, sort]);

  return (
    <main className="relative max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="text-center mb-12">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">A garden of remembrance</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--rb-text-heading)]">
          Souls remembered<br/><span className="italic">by their families.</span>
        </h1>
        <div className="ornament mt-6"><span>✦</span></div>
      </div>

      {/* Search + filters */}
      <div className="glass p-5 md:p-6 rounded-sm mb-10 grid md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-6">
          <label className="rb-label">Search</label>
          <input
            className="rb-input"
            placeholder="Search by name, family, or breed..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="gallery-search"
          />
        </div>
        <div className="md:col-span-3">
          <label className="rb-label">Species / breed</label>
          <select className="rb-select" value={species} onChange={(e) => setSpecies(e.target.value)} data-testid="gallery-filter-species">
            {speciesOptions.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All souls" : s === "Dog" ? "Dogs · all breeds" : s === "Cat" ? "Cats · all breeds" : s}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="rb-label">Sort</label>
          <select className="rb-select" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="gallery-sort">
            <option value="recent">Recently added</option>
            <option value="name">Alphabetical</option>
          </select>
        </div>
      </div>

      <div className="text-center text-[12px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] mb-6">
        {filtered.length} {filtered.length === 1 ? "soul" : "souls"} remembered
      </div>

      {loading ? (
        <div className="text-center font-serif-display italic text-[var(--rb-text-muted)]">Gathering the garden…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center font-serif-display italic text-[var(--rb-text-muted)] py-16">
          {q || species !== "all" ? "No souls match — try a different name." : "The garden is still — be the first to plant a memorial."}
          {!q && species === "all" && (
            <div className="mt-8"><Link to="/create" className="btn-gold">Begin a memorial</Link></div>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((m) => (
            <Link to={`/memorial/${m.share_slug}`} key={m.id} className="memorial-card group block">
              <div className="aspect-square bg-gradient-to-br from-[#FDFBF7] via-[#F2E3C6]/40 to-[#FCFCFA] relative overflow-hidden">
                {m.has_image ? (
                  <ImageThumb slug={m.share_slug} />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg width="64" height="64" viewBox="0 0 64 64" className="opacity-40"><path d="M6 48 A26 26 0 0 1 58 48" fill="none" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round"/></svg>
                  </div>
                )}
              </div>
              <div className="p-6 text-center">
                <div className="tracking-celestial text-[var(--rb-gold)] text-[10px] mb-2">In loving memory</div>
                <div className="font-serif-display italic text-2xl text-[var(--rb-text-heading)]">{m.pet_name}</div>
                {m.dates && <div className="font-serif-display italic text-sm text-[var(--rb-text-muted)] mt-1">{m.dates}</div>}
                {m.owner_display_name && <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] mt-3">— {m.owner_display_name}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

function ImageThumb({ slug }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    apiClient.get(`/memorials/${slug}`).then(({ data }) => {
      if (data.scenery_image_base64) {
        setSrc(`data:${data.scenery_mime_type || "image/png"};base64,${data.scenery_image_base64}`);
      }
    }).catch(() => {});
  }, [slug]);
  if (!src) return null;
  return <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />;
}
